import React, { useState } from 'react';
import SwipeableModal from '../../../components/SwipeableModal';
import Icon from '../../../components/AppIcon';

const FAQsModal = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const faqs = [
    {
      id: 1,
      category: 'Getting Started',
      question: 'What is MyBodyToday?',
      answer: 'MyBodyToday is a comprehensive health tracking app designed to help you monitor symptoms, identify patterns, and share insights with healthcare providers. It\'s perfect for individuals managing chronic conditions or caregivers supporting loved ones.'
    },
    {
      id: 2,
      category: 'Getting Started',
      question: 'Is MyBodyToday free to use?',
      answer: 'Yes! The Free Plan is completely free forever and includes unlimited symptom tracking and symptom timeline view — no credit card required. Upgrade to Premium (£4.99/month) to unlock doctor consultation summaries, patient notes, trigger tracking, and doctor appointment reminders with 3-tier email alerts.'
    },
    {
      id: 3,
      category: 'Getting Started',
      question: 'How do I get started?',
      answer: 'Simply sign up with your email, complete your profile, choose your user type (Individual or Caregiver), and start your first daily check-in. Our interactive onboarding wizard will guide you through the process. Email verification is required to access all features.'
    },
    {
      id: 4,
      category: 'Daily Check-In',
      question: 'How often should I log symptoms?',
      answer: 'We recommend daily check-ins for the most accurate tracking. However, you can log symptoms as frequently as needed. The more consistent your tracking, the better insights you\'ll gain.'
    },
    {
      id: 5,
      category: 'Daily Check-In',
      question: 'Can I add photos or voice notes?',
      answer: 'Yes! You can attach photos (like rashes or swelling) and record voice notes to provide additional context for your symptoms. This helps create a more complete health record. All attachments are automatically encrypted for your privacy.'
    },
    {
      id: 6,
      category: 'Daily Check-In',
      question: 'What if I forget to log a day?',
      answer: 'No problem! You can backdate entries to fill in missed days. Just select the date you want to log for in the calendar view.'
    },
    {
      id: 7,
      category: 'Daily Check-In',
      question: 'Can I track symptoms for multiple people?',
      answer: 'Yes! Premium plan users can track up to 3 caregiver sub-profiles. On page 1 of the daily check-in, you\'ll see a ProfileSelector where you can choose who you\'re recording symptoms for. Add dependents through User Relationship Management.'
    },
    {
      id: 8,
      category: 'Data & Export',
      question: 'How do I share data with my doctor?',
      answer: 'Go to Doctor Summary Export, select your date range, and generate a professional PDF report. You can email it directly to your doctor or download it for your appointment. All exported data is decrypted automatically for readability.'
    },
    {
      id: 9,
      category: 'Data & Export',
      question: 'What formats can I export data in?',
      answer: 'You can export your symptom data as PDF (formatted reports) or CSV (spreadsheet format for analysis). Premium users get unlimited exports.'
    },
    {
      id: 10,
      category: 'Data & Export',
      question: 'Can I export all my data at once?',
      answer: 'Yes! Go to Settings & Privacy > Data Management and select "Export All Data" to download your complete health record. This includes all symptom entries, medications, notes, and attachments.'
    },
    {
      id: 11,
      category: 'Subscription',
      question: 'What\'s included in the Free Plan?',
      answer: 'The Free Plan is completely free forever and includes unlimited symptom tracking and symptom timeline view. No credit card required, no time limits.'
    },
    {
      id: 12,
      category: 'Subscription',
      question: 'What\'s included in the Premium plan?',
      answer: 'Premium (£4.99/month) includes everything in the Free Plan plus: doctor consultation summary, patient notes, trigger tracking, and doctor appointment reminders (save up to 3 upcoming appointments with automatic email reminders at 7 days, 1 day, and 2 hours before each appointment).'
    },
    {
      id: 13,
      category: 'Subscription',
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes! You can cancel anytime from Subscription Management. You\'ll retain access until the end of your billing period, and your data is never deleted. You can reactivate your subscription at any time.'
    },
    {
      id: 14,
      category: 'Subscription',
      question: 'Do you offer annual billing?',
      answer: 'Yes! Annual plans offer savings compared to monthly billing. Annual subscribers also get priority support and early access to new features.'
    },
    {
      id: 15,
      category: 'Subscription',
      question: 'What happens after my 14-day trial ends?',
      answer: 'After your trial ends, you\'ll automatically move to the Free plan unless you choose to upgrade. Your data is preserved, but you\'ll have access to limited features (symptom tracking and timeline only). You can upgrade to Premium at any time to unlock full features.'
    },
    {
      id: 16,
      category: 'Doctor Appointments',
      question: 'Can I manage my doctor appointments in MyBodyToday?',
      answer: 'Yes! Premium users can save up to 3 upcoming doctor appointments and receive automatic email reminders 7 days, 1 day, and 2 hours before each appointment. Each reminder includes a direct link to generate your consultation summary so you arrive fully prepared.'
    },
    {
      id: 17,
      category: 'Doctor Appointments',
      question: 'How do I unlock the appointment reminders feature?',
      answer: 'Appointment reminders are available on the Premium plan at £4.99/month. Alternatively, refer 10 friends to MyBodyToday and unlock all Premium features permanently for free.'
    },
    {
      id: 18,
      category: 'Caregiver Features',
      question: 'How do I add dependents as a caregiver?',
      answer: 'Go to User Relationship Management from the bottom navigation menu and click "Add Dependent". Fill out their information including name, relationship type, and optional email. Once added, they\'ll appear in the ProfileSelector on page 1 of your daily check-in.'
    },
    {
      id: 19,
      category: 'Caregiver Features',
      question: 'Can dependents see my data?',
      answer: 'No. Caregiver relationships are one-way. You can view and manage their symptom data, but they cannot see yours unless you explicitly share it. All data is encrypted separately for each profile.'
    },
    {
      id: 20,
      category: 'Caregiver Features',
      question: 'How do caregiver alerts work?',
      answer: 'You can configure alert thresholds for each dependent in the Critical Alerts Dashboard. When they log symptoms exceeding those thresholds (e.g., pain level 8+, severe symptoms), you\'ll receive an instant notification via email and in-app alert.'
    },
    {
      id: 21,
      category: 'Referral Program',
      question: 'How does the referral program work?',
      answer: 'Share your unique referral link with friends. Refer 5 friends to unlock the Doctor Consultation Summary report for free. Refer 10 friends to unlock the full Premium plan permanently for free — including doctor consultation summary, patient notes, trigger tracking, and appointment reminders. You can also upgrade directly for £4.99/month.'
    },
    {
      id: 22,
      category: 'Referral Program',
      question: 'How do I share my referral link?',
      answer: 'You can share your unique referral link via one-click social sharing buttons (WhatsApp, Twitter, Facebook, Email), copy the link directly to your clipboard, or display your QR code for in-person sharing.'
    },
    {
      id: 23,
      category: 'Privacy & Security',
      question: 'Is my health data secure?',
      answer: 'Yes. We use mandatory military-grade encryption (AES-256) for all sensitive health data. Your symptom entries, medications, notes, photos, and voice recordings are automatically encrypted on your device before being stored. Encryption is transparent and requires no passwords.'
    },
    {
      id: 24,
      category: 'Privacy & Security',
      question: 'Can I enable two-factor authentication?',
      answer: 'Yes! Go to Settings & Privacy > Security and enable 2FA via authenticator app or SMS for an extra layer of account protection.'
    },
    {
      id: 25,
      category: 'Privacy & Security',
      question: 'How do I delete my account?',
      answer: 'Go to Settings & Privacy > Data Management > Delete Account. This permanently removes all your data within 30 days. You can export your data first if needed.'
    },
    {
      id: 26,
      category: 'Analytics',
      question: 'What are pattern recognition insights?',
      answer: 'Our AI analyzes your symptom history to identify recurring patterns, correlations between symptoms, and potential triggers. Available for Premium plan subscribers.'
    },
    {
      id: 27,
      category: 'Technical',
      question: 'Which devices are supported?',
      answer: 'MyBodyToday works on any device with a modern web browser (Chrome, Safari, Firefox, Edge). We\'re mobile-optimized and working on native iOS and Android apps.'
    },
    {
      id: 28,
      category: 'Technical',
      question: 'Why do I need to verify my email?',
      answer: 'Email verification is required to access all features of MyBodyToday. This ensures account security, enables password recovery, and allows us to send important notifications. Check your inbox and spam folder for the verification link.'
    }
  ];

  const filteredFaqs = faqs?.filter(
    (faq) =>
      faq?.question?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      faq?.answer?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      faq?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const categories = [...new Set(filteredFaqs?.map((faq) => faq?.category))];

  return (
    <SwipeableModal isOpen={isOpen} onClose={onClose} title="Frequently Asked Questions" maxWidth="max-w-2xl">
      <div className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            type="text"
            placeholder="Search FAQs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full pl-9 pr-4 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* FAQ List */}
        <div className="space-y-6 max-h-[60vh] overflow-y-auto">
          {categories?.map((category) => (
            <div key={category}>
              <h3 className="text-xs font-bold text-primary uppercase tracking-wider mb-3 px-1">{category}</h3>
              <div className="space-y-2">
                {filteredFaqs
                  ?.filter((faq) => faq?.category === category)
                  ?.map((faq) => (
                    <div
                      key={faq?.id}
                      className="border border-border rounded-lg overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedId(expandedId === faq?.id ? null : faq?.id)}
                        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-muted/50 transition-colors"
                      >
                        <span className="text-sm font-semibold text-foreground pr-4">{faq?.question}</span>
                        <Icon
                          name="ChevronDown"
                          size={16}
                          className={`flex-shrink-0 text-primary transition-transform ${expandedId === faq?.id ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {expandedId === faq?.id && (
                        <div className="px-4 pb-4">
                          <p className="text-sm text-muted-foreground leading-relaxed">{faq?.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          ))}

          {filteredFaqs?.length === 0 && (
            <div className="text-center py-8">
              <Icon name="Search" size={32} className="mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground text-sm">No FAQs found matching your search.</p>
            </div>
          )}
        </div>
      </div>
    </SwipeableModal>
  );
};

export default FAQsModal;